import { Settings } from './Settings'
import { ValueOf } from '@/types'
import { deepEqual, useState } from '@codeleap/common'
import { globalState } from '@codeleap/store'
import { resetLessonStore } from '@/stores/lesson'
import { LocalStorageKeys } from './constants'

export const SERVERS = {
  Development: 'development',
  Staging: 'staging',
  Production: 'production',
  Custom: 'custom'
} as const

export const SERVER_OPTIONS = Object.entries(SERVERS).map(([label, value]) => ({
  label,
  value
}))

type Server = ValueOf<typeof SERVERS>

type TestPanelStore = {
  server: Server
  customServerUrl?: string
}

const getDefaultServer = () => {
  const url = process.env.GATSBY_API_URL

  if (url === Settings.ServerURLS.development) {
    return SERVERS.Development
  }

  return SERVERS.Production
}

export const defaultOptions: TestPanelStore = {
  server: getDefaultServer(),
  customServerUrl: ''
}

const EDITORS = {
  SYSTEM: 'system',
  USER: 'user'
} as const

export const serverUrlValidator = /^https?:\/\/[a-zA-Z0-9.-]+(?::\d+)?\/$/

type Editors = ValueOf<typeof EDITORS> | null

const defaultStoreValue = {
  ...defaultOptions,
  enabledBy: null
}

const testPanelStore = globalState<TestPanelStore & { enabledBy: Editors }>(defaultStoreValue, {
  persistKey: LocalStorageKeys.DEV_MODE
})

const getStoreValue = () => {
  if (typeof window === 'undefined') return defaultStoreValue
  const value = testPanelStore.get()
  if (!value?.server) testPanelStore.set({ server: defaultStoreValue?.server })
  return testPanelStore.get() ?? defaultStoreValue
}

const getInitialOptions = () => {
  const { enabledBy: _, ...options } = getStoreValue()
  return options
}

export function getEnv(): TestPanelStore {
  const value = getStoreValue()

  if (value.enabledBy) {
    if (value.server == SERVERS.Custom && !serverUrlValidator.test(value.customServerUrl)) {
      value.server = SERVERS.Production
    }

    return value
  }

  return defaultOptions
}

export function setEnabled(by: Editors = null) {
  testPanelStore.set({
    enabledBy: by
  })

  const isProductionLike = !by

  // analytics().setAnalyticsCollectionEnabled(isProductionLike)
}

export function toggleEnabled(by: Editors = EDITORS.SYSTEM) {
  setEnabled(isEnabled() ? null : by)
}

export function isEnabled() {
  return getStoreValue()?.enabledBy
}

export function setServer(server: Server) {
  if (!isEnabled()) return

  testPanelStore.set({
    server: server
  })

  resetLessonStore()
}

export function use() {
  const value = testPanelStore.use()

  const { enabledBy, ...options } = value ?? defaultStoreValue

  const changed = !deepEqual(options, getInitialOptions())

  const [customUrl, setCustomUrl] = useState(options.customServerUrl)

  return {
    enabled: !!enabledBy,
    enabledByUser: enabledBy == EDITORS.USER,
    options,
    changed,
    customUrl,
    setCustomUrl
  }
}

export function isProductionLike() {
  const env = getEnv()

  const prodServers = [SERVERS.Production, SERVERS.Staging] as unknown as Server[]

  return prodServers.includes(env.server)
}

export function isProduction() {
  const env = getEnv()

  return SERVERS.Production === env.server
}

export function getServerUrl() {
  const env = getEnv()

  if (env.server == SERVERS.Custom) {
    if (!serverUrlValidator.test(env.customServerUrl)) {
      return Settings.ServerURLS.production
    }

    return env.customServerUrl
  }

  return Settings.ServerURLS[env.server] ?? Settings.ServerURLS.production
}

export function getWebsiteUrl() {
  const env = getEnv()

  return Settings.Website[env.server] ?? Settings.Website.production
}

export function isEnabledBySystem() {
  return getStoreValue()?.enabledBy == 'system'
}

export function isEnabledByUser() {
  return getStoreValue()?.enabledBy == 'user'
}

export function setCustomServerUrl(url: string) {
  if (!url.endsWith('/')) {
    url += '/'
  }

  testPanelStore.set({
    customServerUrl: url
  })
}
